import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { planAPI, routerAPI } from '../services/api';
import api from '../services/api';
import axios from 'axios';
import { Calendar, Plus, ArrowLeft, Info, Wifi, Clock, Shield, Trash2, AlertTriangle } from 'lucide-react';
import { Alert } from '../components/Modal';
import { getRouterColor, getRouterName } from '../utils/routerColors';
import PlanWizardModal from '../components/PlanWizardModal';

// Versión del módulo de Planes (para depuración y seguimiento de cambios)
const PLANS_PAGE_VERSION = 'v1.0.0';

const PlansPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('listar'); // 'crear' o 'listar'
  const [plans, setPlans] = useState([]);
  const [routers, setRouters] = useState([]);
  const [selectedRouterFilter, setSelectedRouterFilter] = useState('all');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('all'); // 'all' | 'time_running' | 'time_paused' | 'data_limit' | 'suspended'
  const [searchQuery, setSearchQuery] = useState(''); // Nuevo: búsqueda
  const [loading, setLoading] = useState(true);
  const [selectedRouter, setSelectedRouter] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    router_id: '',
    name: '',
    plan_type: 'time_running',
    prefix: '',
    vigencia_dias: '',
    vigencia_horas: '',
    vigencia_minutos: '',
    caducidad_dias: '',     // ✅ NUEVO: Caducidad máxima (días)
    caducidad_horas: '',    // ✅ NUEVO: Caducidad máxima (horas)
    caducidad_minutos: '',  // ✅ NUEVO: Caducidad máxima (minutos)
    data_limit_value: '',
    data_limit_unit: 'MB',
    registrar_mac: false,
    velocidad_subida: '1',
    velocidad_subida_unit: 'M',
    velocidad_descarga: '1',
    velocidad_descarga_unit: 'M',
    tipo_usuario: 'pin',
    validate_both_fields: false,
    tipo_codigo: 'alfanumerico',
    longitud_password: '6',
    usa_prefijo: false,
    es_consecutivo: false
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  
  // Estados para eliminación de planes
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Estados para plan duplicado
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicatePlanId, setDuplicatePlanId] = useState(null);
  const [duplicatePlanIsActive, setDuplicatePlanIsActive] = useState(true);
  const [newPlanName, setNewPlanName] = useState('');
  
  // Estado para wizard
  const [showWizard, setShowWizard] = useState(false);

  // Estado para modal de copiar plan
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyPlanName, setCopyPlanName] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansRes, routersRes] = await Promise.all([
        api.get('/api/plans', { params: { include_suspended: true } }),  // Incluir suspendidos en página de listado
        routerAPI.getAll()
      ]);
      setPlans(plansRes.data);
      setRouters(routersRes.data);
      
      // Pre-seleccionar el primer router si existe
      if (routersRes.data.length > 0) {
        setFormData(prev => ({ ...prev, router_id: routersRes.data[0].id }));
        setSelectedRouter(routersRes.data[0]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRouterChange = (routerId) => {
    const router = routers.find(r => r.id === routerId);
    setSelectedRouter(router);
    setFormData({ ...formData, router_id: routerId });
  };

  const calculateDurationMinutes = () => {
    const dias = parseInt(formData.vigencia_dias) || 0;
    const horas = parseInt(formData.vigencia_horas) || 0;
    const minutos = parseInt(formData.vigencia_minutos) || 0;
    
    return (dias * 24 * 60) + (horas * 60) + minutos;
  };

  const calculateCaducidadDays = () => {
    const dias = parseInt(formData.caducidad_dias) || 0;
    const horas = parseInt(formData.caducidad_horas) || 0;
    const minutos = parseInt(formData.caducidad_minutos) || 0;
    
    // Convertir todo a días (decimal)
    return dias + (horas / 24) + (minutos / 1440);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      // Validación 1: Router seleccionado
      if (!formData.router_id) {
        setError('Debe seleccionar un router');
        setSubmitting(false);
        return;
      }

      // Validación 2: Nombre del plan
      if (!formData.name || formData.name.trim() === '') {
        setError('Debe especificar un nombre para el plan');
        setSubmitting(false);
        return;
      }

      // Validación 3: Prefijo (obligatorio para userpass y para pin con prefijo)
      if (formData.tipo_usuario === 'userpass' || (formData.tipo_usuario === 'pin' && formData.usa_prefijo)) {
        if (!formData.prefix || formData.prefix.trim() === '') {
          setError('Debe especificar un prefijo');
          setSubmitting(false);
          return;
        }

        // Validación 3b: Prefijo sin espacios
        if (formData.prefix.includes(' ')) {
          setError('El prefijo no puede contener espacios');
          setSubmitting(false);
          return;
        }
      }

      // Validación 4: Vigencia o Límite de Datos
      if (formData.plan_type === 'data_limit') {
        // Para "Por Consumo", validar límite de datos y caducidad
        if (!formData.data_limit_value || parseFloat(formData.data_limit_value) <= 0) {
          setError('Debe especificar un límite de datos válido');
          setSubmitting(false);
          return;
        }
        const caducidadDays = calculateCaducidadDays();
        if (caducidadDays === 0) {
          setError('Debe especificar una caducidad máxima para el plan por consumo');
          setSubmitting(false);
          return;
        }
      } else if (formData.plan_type === 'time_running') {
        // Para tiempo corrido, solo validar duración
        const durationMinutes = calculateDurationMinutes();
        if (durationMinutes === 0) {
          setError('Debe especificar al menos una vigencia (días, horas o minutos)');
          setSubmitting(false);
          return;
        }
      } else if (formData.plan_type === 'time_paused') {
        // Para tiempo pausado, validar duración Y caducidad
        const durationMinutes = calculateDurationMinutes();
        if (durationMinutes === 0) {
          setError('Debe especificar la duración de uso (días, horas o minutos)');
          setSubmitting(false);
          return;
        }
        const caducidadDays = calculateCaducidadDays();
        if (caducidadDays === 0) {
          setError('Debe especificar una caducidad máxima para el plan pausado');
          setSubmitting(false);
          return;
        }
      }

      // Validación 5: Velocidades
      if (!formData.velocidad_subida || parseFloat(formData.velocidad_subida) <= 0) {
        setError('Debe especificar una velocidad de subida válida');
        setSubmitting(false);
        return;
      }

      if (!formData.velocidad_descarga || parseFloat(formData.velocidad_descarga) <= 0) {
        setError('Debe especificar una velocidad de descarga válida');
        setSubmitting(false);
        return;
      }

      // Validación 6: Longitud del password
      const passwordLength = parseInt(formData.longitud_password);
      if (!passwordLength || passwordLength < 4 || passwordLength > 12) {
        setError('La longitud del password debe estar entre 4 y 12 caracteres');
        setSubmitting(false);
        return;
      }

      const rateLimit = `${formData.velocidad_subida}${formData.velocidad_subida_unit}/${formData.velocidad_descarga}${formData.velocidad_descarga_unit}`;

      const planPayload = {
        router_id: formData.router_id,
        name: formData.name,
        plan_type: formData.plan_type,
        price: 0,
        rate_limit: rateLimit,
        authentication_type: formData.tipo_usuario === 'pin' ? 'pin' : 'userpass',
        prefix: formData.prefix || null,
        usa_prefijo: formData.usa_prefijo,
        tipo_codigo: formData.tipo_codigo,
        longitud_password: parseInt(formData.longitud_password, 10),
        es_consecutivo: formData.es_consecutivo
      };

      // Agregar campos según tipo de plan
      if (formData.plan_type === 'data_limit') {
        // Plan por consumo: límite de datos + caducidad máxima
        planPayload.data_limit = `${formData.data_limit_value}${formData.data_limit_unit}`;
        const caducidadDays = calculateCaducidadDays();
        planPayload.validity_days = caducidadDays;  // Redondear hacia arriba
      } else if (formData.plan_type === 'time_running') {
        // Plan tiempo corrido: solo duración de uso
        const durationMinutes = calculateDurationMinutes();
        planPayload.duration_minutes = durationMinutes;
        // NO necesita validity_days porque expira al terminar el tiempo
      } else if (formData.plan_type === 'time_paused') {
        // Plan tiempo pausado: duración de uso + caducidad máxima
        const durationMinutes = calculateDurationMinutes();
        planPayload.duration_minutes = durationMinutes;
        const caducidadDays = calculateCaducidadDays();
        planPayload.validity_days = caducidadDays;  // Redondear hacia arriba
      }

      await planAPI.create(planPayload);
      
      // Si llegamos aquí, el plan se creó exitosamente
      // Resetear formulario
      setFormData({
        id: null,
        router_id: routers[0]?.id || '',
        name: '',
        plan_type: 'time_running',
        prefix: '',
        vigencia_dias: '',
        vigencia_horas: '',
        vigencia_minutos: '',
        caducidad_dias: '',
        caducidad_horas: '',
        caducidad_minutos: '',
        data_limit_value: '',
        data_limit_unit: 'MB',
        registrar_mac: false,
        velocidad_subida: '1',
        velocidad_subida_unit: 'M',
        velocidad_descarga: '1',
        velocidad_descarga_unit: 'M',
        tipo_usuario: 'pin',
        tipo_codigo: 'alfanumerico',
        longitud_password: '6',
        usa_prefijo: false,
        es_consecutivo: false
      });
      
      setActiveTab('listar');
      loadData();
      
      setAlert({
        type: 'success',
        title: 'Plan Creado',
        message: 'El plan de internet ha sido creado exitosamente y está listo para asignar a vendedores.'
      });
    } catch (err) {
      console.error('Error creating plan:', err);
      
      // Detectar si es un error de plan duplicado (409 Conflict)
      if (err.response?.status === 409 && err.response?.data?.detail?.error === 'PLAN_ALREADY_EXISTS') {
        setDuplicatePlanId(err.response.data.detail.existing_plan_id);
        setDuplicatePlanIsActive(err.response.data.detail.is_active);
        setShowDuplicateModal(true);
        setSubmitting(false);
        return;
      }
      
      const errorMessage = err.response?.data?.detail?.message || err.response?.data?.detail || err.message || 'Error al crear plan';
      setError(errorMessage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateExistingPlan = async () => {
    setSubmitting(true);
    setError('');
    
    try {
      const rateLimit = `${formData.velocidad_subida}${formData.velocidad_subida_unit}/${formData.velocidad_descarga}${formData.velocidad_descarga_unit}`;

      const planPayload = {
        router_id: formData.router_id,
        name: formData.name,
        plan_type: formData.plan_type,
        price: 0,
        rate_limit: rateLimit,
        authentication_type: formData.tipo_usuario === 'pin' ? 'pin' : 'userpass',
        prefix: formData.prefix || null,
        usa_prefijo: formData.usa_prefijo,
        tipo_codigo: formData.tipo_codigo,
        longitud_password: parseInt(formData.longitud_password, 10),
        es_consecutivo: formData.es_consecutivo
      };

      // Agregar campos según tipo de plan
      if (formData.plan_type === 'data_limit') {
        planPayload.data_limit = `${formData.data_limit_value}${formData.data_limit_unit}`;
        const caducidadDays = calculateCaducidadDays();
        planPayload.validity_days = caducidadDays;
      } else if (formData.plan_type === 'time_running') {
        const durationMinutes = calculateDurationMinutes();
        planPayload.duration_minutes = durationMinutes;
      } else if (formData.plan_type === 'time_paused') {
        const durationMinutes = calculateDurationMinutes();
        planPayload.duration_minutes = durationMinutes;
        const caducidadDays = calculateCaducidadDays();
        planPayload.validity_days = caducidadDays;
      }

      // Actualizar plan existente (usando full-update para recrear en Mikrotik)
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/plans/${duplicatePlanId}/full-update`, planPayload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setShowDuplicateModal(false);
      setDuplicatePlanId(null);
      setDuplicatePlanIsActive(true);
      
      // Resetear formulario
      setFormData({
        id: null,
        router_id: routers[0]?.id || '',
        name: '',
        plan_type: 'time_running',
        prefix: '',
        vigencia_dias: '',
        vigencia_horas: '',
        vigencia_minutos: '',
        caducidad_dias: '',
        caducidad_horas: '',
        caducidad_minutos: '',
        data_limit_value: '',
        data_limit_unit: 'MB',
        registrar_mac: false,
        velocidad_subida: '1',
        velocidad_subida_unit: 'M',
        velocidad_descarga: '1',
        velocidad_descarga_unit: 'M',
        tipo_usuario: 'pin',
        tipo_codigo: 'alfanumerico',
        longitud_password: '6',
        usa_prefijo: false,
        es_consecutivo: false
      });
      
      setActiveTab('listar');
      loadData();
      
      setAlert({
        type: 'success',
        title: 'Plan Actualizado',
        message: 'El plan existente ha sido actualizado con los nuevos valores.'
      });
    } catch (err) {
      console.error('Error updating plan:', err);
      setError(err.response?.data?.detail || err.message || 'Error al actualizar plan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateWithNewName = async () => {
    if (!newPlanName || newPlanName.trim() === '') {
      setError('Debe especificar un nombre para el nuevo plan');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const rateLimit = `${formData.velocidad_subida}${formData.velocidad_subida_unit}/${formData.velocidad_descarga}${formData.velocidad_descarga_unit}`;

      const planPayload = {
        router_id: formData.router_id,
        name: newPlanName.toUpperCase(),  // Usar el nuevo nombre
        plan_type: formData.plan_type,
        price: 0,
        rate_limit: rateLimit,
        authentication_type: formData.tipo_usuario === 'pin' ? 'pin' : 'userpass',
        prefix: formData.prefix || null,
        usa_prefijo: formData.usa_prefijo,
        tipo_codigo: formData.tipo_codigo,
        longitud_password: parseInt(formData.longitud_password, 10),
        es_consecutivo: formData.es_consecutivo
      };

      // Agregar campos según tipo de plan
      if (formData.plan_type === 'data_limit') {
        planPayload.data_limit = `${formData.data_limit_value}${formData.data_limit_unit}`;
        const caducidadDays = calculateCaducidadDays();
        planPayload.validity_days = caducidadDays;
      } else if (formData.plan_type === 'time_running') {
        const durationMinutes = calculateDurationMinutes();
        planPayload.duration_minutes = durationMinutes;
      } else if (formData.plan_type === 'time_paused') {
        const durationMinutes = calculateDurationMinutes();
        planPayload.duration_minutes = durationMinutes;
        const caducidadDays = calculateCaducidadDays();
        planPayload.validity_days = caducidadDays;
      }

      // Crear nuevo plan con el nombre diferente
      await planAPI.create(planPayload);
      
      setShowDuplicateModal(false);
      setDuplicatePlanId(null);
      setDuplicatePlanIsActive(true);
      setNewPlanName('');
      
      // Resetear formulario
      setFormData({
        id: null,
        router_id: routers[0]?.id || '',
        name: '',
        plan_type: 'time_running',
        prefix: '',
        vigencia_dias: '',
        vigencia_horas: '',
        vigencia_minutos: '',
        caducidad_dias: '',
        caducidad_horas: '',
        caducidad_minutos: '',
        data_limit_value: '',
        data_limit_unit: 'MB',
        registrar_mac: false,
        velocidad_subida: '1',
        velocidad_subida_unit: 'M',
        velocidad_descarga: '1',
        velocidad_descarga_unit: 'M',
        tipo_usuario: 'pin',
        tipo_codigo: 'alfanumerico',
        longitud_password: '6',
        usa_prefijo: false,
        es_consecutivo: false
      });
      
      setActiveTab('listar');
      loadData();
      
      setAlert({
        type: 'success',
        title: 'Plan Creado',
        message: `El plan "${newPlanName}" ha sido creado exitosamente.`
      });
    } catch (err) {
      console.error('Error creating new plan:', err);
      setError(err.response?.data?.detail || err.message || 'Error al crear nuevo plan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyPlan = async () => {
    if (!copyPlanName || copyPlanName.trim() === '') {
      setError('Debe especificar un nombre para el nuevo plan');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const rateLimit = `${formData.velocidad_subida}${formData.velocidad_subida_unit}/${formData.velocidad_descarga}${formData.velocidad_descarga_unit}`;

      const planPayload = {
        router_id: formData.router_id,
        name: copyPlanName.toUpperCase(),
        plan_type: formData.plan_type,
        price: 0,
        rate_limit: rateLimit,
        authentication_type: formData.tipo_usuario === 'pin' ? 'pin' : 'userpass',
        prefix: formData.prefix || null,
        usa_prefijo: formData.usa_prefijo,
        tipo_codigo: formData.tipo_codigo,
        longitud_password: parseInt(formData.longitud_password, 10),
        es_consecutivo: formData.es_consecutivo
      };

      // Agregar campos según tipo de plan
      if (formData.plan_type === 'data_limit') {
        planPayload.data_limit = `${formData.data_limit_value}${formData.data_limit_unit}`;
        const caducidadDays = calculateCaducidadDays();
        planPayload.validity_days = caducidadDays;
      } else if (formData.plan_type === 'time_running') {
        const durationMinutes = calculateDurationMinutes();
        planPayload.duration_minutes = durationMinutes;
      } else if (formData.plan_type === 'time_paused') {
        const durationMinutes = calculateDurationMinutes();
        planPayload.duration_minutes = durationMinutes;
        const caducidadDays = calculateCaducidadDays();
        planPayload.validity_days = caducidadDays;
      }

      await planAPI.create(planPayload);
      
      setShowCopyModal(false);
      setCopyPlanName('');
      
      // Resetear formulario
      setFormData({
        id: null,
        router_id: routers[0]?.id || '',
        name: '',
        plan_type: 'time_running',
        prefix: '',
        vigencia_dias: '',
        vigencia_horas: '',
        vigencia_minutos: '',
        caducidad_dias: '',
        caducidad_horas: '',
        caducidad_minutos: '',
        data_limit_value: '',
        data_limit_unit: 'MB',
        registrar_mac: false,
        velocidad_subida: '1',
        velocidad_subida_unit: 'M',
        velocidad_descarga: '1',
        velocidad_descarga_unit: 'M',
        tipo_usuario: 'pin',
        tipo_codigo: 'alfanumerico',
        longitud_password: '6',
        usa_prefijo: false,
        es_consecutivo: false
      });
      
      setActiveTab('listar');
      loadData();
      
      setAlert({
        type: 'success',
        title: 'Plan Copiado',
        message: `El plan "${copyPlanName}" ha sido creado exitosamente como copia.`
      });
    } catch (err) {
      console.error('Error copying plan:', err);
      
      // Detectar si es un error de plan duplicado
      if (err.response?.status === 409 && err.response?.data?.detail?.error === 'PLAN_ALREADY_EXISTS') {
        setError(`Ya existe un plan con el nombre "${copyPlanName}". Por favor elige otro nombre.`);
      } else {
        setError(err.response?.data?.detail || err.message || 'Error al copiar plan');
      }
    } finally {
      setSubmitting(false);
    }
  };


  const handleWizardSubmit = async (wizardData) => {
    setError('');
    setSubmitting(true);

    try {
      const rateLimit = `${wizardData.velocidad_subida}${wizardData.velocidad_subida_unit}/${wizardData.velocidad_descarga}${wizardData.velocidad_descarga_unit}`;

      const planPayload = {
        router_id: wizardData.router_id,
        name: wizardData.name.toUpperCase(),
        plan_type: wizardData.plan_type,
        price: 0,
        rate_limit: rateLimit,
        authentication_type: wizardData.tipo_usuario === 'pin' ? 'pin' : 'userpass',
        validate_both_fields: wizardData.validate_both_fields !== undefined ? wizardData.validate_both_fields : false,
        prefix: wizardData.prefix || null,
        usa_prefijo: wizardData.usa_prefijo,
        tipo_codigo: wizardData.tipo_codigo,
        longitud_password: parseInt(wizardData.longitud_password, 10),
        es_consecutivo: wizardData.es_consecutivo
      };

      // Calcular duration_minutes
      const dias = parseInt(wizardData.vigencia_dias) || 0;
      const horas = parseInt(wizardData.vigencia_horas) || 0;
      const minutos = parseInt(wizardData.vigencia_minutos) || 0;
      const durationMinutes = (dias * 24 * 60) + (horas * 60) + minutos;

      // Calcular validity_days (caducidad)
      const caducidadDias = parseInt(wizardData.caducidad_dias) || 0;
      const caducidadHoras = parseInt(wizardData.caducidad_horas) || 0;
      const caducidadMinutos = parseInt(wizardData.caducidad_minutos) || 0;
      const caducidadTotalMinutes = (caducidadDias * 24 * 60) + (caducidadHoras * 60) + caducidadMinutos;
      const caducidadTotalDays = caducidadTotalMinutes / 1440;  // No redondear aquí

      // Agregar campos según tipo de plan
      if (wizardData.plan_type === 'time_running') {
        // Tiempo corrido: solo duration_minutes
        planPayload.duration_minutes = durationMinutes;
      } else if (wizardData.plan_type === 'time_paused') {
        // Tiempo pausado: duration_minutes + validity_days (decimal exacto)
        planPayload.duration_minutes = durationMinutes;
        planPayload.validity_days = caducidadTotalDays;  // Enviar valor decimal exacto
      } else if (wizardData.plan_type === 'data_limit') {
        // Por consumo: data_limit + validity_days (decimal exacto)
        planPayload.data_limit = `${wizardData.data_limit_value}${wizardData.data_limit_unit}`;
        planPayload.validity_days = caducidadTotalDays;  // Enviar valor decimal exacto
      }

      await planAPI.create(planPayload);

      // Si llegamos aquí, el plan se creó exitosamente
      setShowWizard(false);
      loadData();

      setAlert({
        type: 'success',
        title: 'Plan Creado',
        message: `El plan "${wizardData.name}" ha sido creado exitosamente.`
      });
    } catch (err) {
      console.error('Error creating plan:', err);

      // Detectar si es un error de plan duplicado (409 Conflict)
      if (err.response?.status === 409 && err.response?.data?.detail?.error === 'PLAN_ALREADY_EXISTS') {
        setDuplicatePlanId(err.response.data.detail.existing_plan_id);
        setDuplicatePlanIsActive(err.response.data.detail.is_active);
        
        // Guardar los datos del wizard para usarlos en el modal de duplicado
        setFormData({
          ...formData,
          ...wizardData
        });
        
        setShowWizard(false);
        setShowDuplicateModal(true);
        setSubmitting(false);
        return;
      }

      const errorMessage = err.response?.data?.detail?.message || err.response?.data?.detail || err.message || 'Error al crear plan';
      setError(errorMessage);
      setAlert({
        type: 'error',
        title: 'Error',
        message: errorMessage
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = async (plan, e) => {
    e.stopPropagation(); // Evitar que se abra el formulario de edición
    
    try {
      // Obtener información detallada antes de mostrar modal
      const response = await planAPI.get(`/${plan.id}/delete-info`);
      setDeleteInfo(response.data);
      setPlanToDelete(plan);
      setShowDeleteModal(true);
    } catch (error) {
      console.error('Error obteniendo información del plan:', error);
      setAlert({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.detail || 'No se pudo obtener información del plan'
      });
    }
  };

  const confirmDeletePlan = async () => {
    if (!planToDelete) return;
    
    setDeleting(true);
    try {
      const response = await planAPI.post(`/${planToDelete.id}/confirm-delete`);
      
      setAlert({
        type: 'success',
        title: !planToDelete.is_active ? 'Plan Reactivado' : 'Plan Suspendido',
        message: response.data.message || (!planToDelete.is_active ? 'El plan ha sido reactivado exitosamente' : 'El plan ha sido suspendido exitosamente')
      });
      
      // Cerrar modal y recargar planes
      setShowDeleteModal(false);
      setPlanToDelete(null);
      setDeleteInfo(null);
      loadData();
      
    } catch (error) {
      console.error('Error eliminando plan:', error);
      setAlert({
        type: 'error',
        title: 'Error al Eliminar',
        message: error.response?.data?.detail || 'No se pudo eliminar el plan'
      });
    } finally {
      setDeleting(false);
    }
  };

  const planTypeLabels = {
    time_running: 'Tiempo Corrido',
    time_paused: 'Tiempo Pausado',
    data_limit: 'Por Consumo'
  };

  const getPlanTypeBadge = (planType) => {
    switch(planType) {
      case 'time_running':
        return {
          label: 'Tiempo Corrido',
          icon: '⏱️',
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-800',
          borderColor: 'border-blue-300'
        };
      case 'time_paused':
        return {
          label: 'Tiempo Pausado',
          icon: '⏸️',
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-800',
          borderColor: 'border-orange-300'
        };
      case 'data_limit':
        return {
          label: 'Por Consumo',
          icon: '📊',
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          borderColor: 'border-green-300'
        };
      default:
        return {
          label: 'Desconocido',
          icon: '❓',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-300'
        };
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark ? 'bg-gray-900' : 'bg-gray-100'
    }`}>
      {/* Header */}
      <header className={`shadow-sm transition-colors duration-300 ${
        isDark ? 'bg-gray-800 border-b border-gray-700' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-900'
                }`}
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Planes Para Hotspot
                  <span className="ml-2 text-xs font-semibold text-blue-400">[{PLANS_PAGE_VERSION}]</span>
                </h1>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Crea y gestiona tus planes de internet</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className={`flex gap-2 mb-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setActiveTab('listar')}
            style={{
              color: activeTab === 'listar' 
                ? '#2563eb' 
                : isDark ? '#d1d5db' : '#4b5563'
            }}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'listar'
                ? 'border-blue-600'
                : 'border-transparent hover:opacity-80'
            }`}
          >
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Enlistar Planes
            </div>
          </button>
          <button
            onClick={() => setShowWizard(true)}
            className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Crear Plan
          </button>
        </div>

        {/* Crear Plan Tab */}
        {activeTab === 'crear' && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-bold mb-6">Crear Plan Para Hotspot</h2>
            
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-3">
                <Info className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Router Version Info */}
            {selectedRouter && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                <Wifi className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-900">
                    RouterOS Detectado: {selectedRouter.version_type?.toUpperCase() || 'v6'}
                  </p>
                  <p className="text-sm text-blue-700">
                    {selectedRouter.name} - Versión {selectedRouter.version || 'Desconocida'}
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Router Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Router *
                </label>
                <select
                  required
                  value={formData.router_id}
                  onChange={(e) => handleRouterChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecciona un router</option>
                  {routers.map((router) => (
                    <option key={router.id} value={router.id}>
                      {router.name} - {router.version_type?.toUpperCase() || 'v6'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tipo de Plan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Plan *
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, plan_type: 'time_running' })}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      formData.plan_type === 'time_running'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <Clock className="w-6 h-6 mx-auto mb-2" />
                    <p className="font-semibold">Tiempo Corrido</p>
                    <p className="text-xs text-gray-600 mt-1">El tiempo corre siempre</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, plan_type: 'time_paused' })}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      formData.plan_type === 'time_paused'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <Shield className="w-6 h-6 mx-auto mb-2" />
                    <p className="font-semibold">Tiempo Pausado</p>
                    <p className="text-xs text-gray-600 mt-1">Se pausa al desconectar</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, plan_type: 'data_limit' })}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      formData.plan_type === 'data_limit'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <Wifi className="w-6 h-6 mx-auto mb-2" />
                    <p className="font-semibold">Por Consumo</p>
                    <p className="text-xs text-gray-600 mt-1">Límite de MB/GB</p>
                  </button>
                </div>
              </div>

              {/* Nombre del Plan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Plan *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: 15 MINUTOS, 1 HORA, etc."
                />
              </div>

              {/* Prefijo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prefijo {(formData.tipo_usuario === 'userpass' || formData.usa_prefijo) ? '*' : '(opcional)'}
                </label>
                <input
                  type="text"
                  required={formData.tipo_usuario === 'userpass' || formData.usa_prefijo}
                  value={formData.prefix}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/\s+/g, '');
                    setFormData({ ...formData, prefix: value });
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: NET, WIFI, CAFE"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.tipo_usuario === 'userpass' 
                    ? 'Se usará para generar usuarios (ej: NET001, NET002...)' 
                    : formData.usa_prefijo
                    ? 'Se incluirá en el PIN (ej: NET123456)'
                    : 'Opcional para PIN sin prefijo'}
                </p>
              </div>

              {/* Límite de Datos (solo para Por Consumo) - DESPUÉS DE PREFIJO */}
              {formData.plan_type === 'data_limit' && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Límite de Datos *
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="number"
                      min="1"
                      value={formData.data_limit_value}
                      onChange={(e) => setFormData({ ...formData, data_limit_value: e.target.value })}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="100"
                    />
                    <select
                      value={formData.data_limit_unit}
                      onChange={(e) => setFormData({ ...formData, data_limit_unit: e.target.value })}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      <option value="KB">KB</option>
                      <option value="MB">MB</option>
                      <option value="GB">GB</option>
                    </select>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    💡 El usuario será desconectado al alcanzar este límite
                  </p>
                </div>
              )}

              {/* Duración de Uso (solo para planes de tiempo) */}
              {formData.plan_type !== 'data_limit' && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.plan_type === 'time_running' ? 'Duración *' : 'Duración de Uso *'}
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Días</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.vigencia_dias}
                        onChange={(e) => setFormData({ ...formData, vigencia_dias: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Horas</label>
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={formData.vigencia_horas}
                        onChange={(e) => setFormData({ ...formData, vigencia_horas: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Minutos</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={formData.vigencia_minutos}
                        onChange={(e) => setFormData({ ...formData, vigencia_minutos: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    {formData.plan_type === 'time_running' 
                      ? '⏱️ El tiempo corre continuamente desde que se conecta' 
                      : '⏸️ Tiempo de uso efectivo (se pausa al desconectar)'}
                  </p>
                </div>
              )}

              {/* Caducidad Máxima (para tiempo pausado y por consumo) */}
              {(formData.plan_type === 'time_paused' || formData.plan_type === 'data_limit') && (
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Caducidad Máxima *
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Días</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.caducidad_dias}
                        onChange={(e) => setFormData({ ...formData, caducidad_dias: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Horas</label>
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={formData.caducidad_horas}
                        onChange={(e) => setFormData({ ...formData, caducidad_horas: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Minutos</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={formData.caducidad_minutos}
                        onChange={(e) => setFormData({ ...formData, caducidad_minutos: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    {formData.plan_type === 'time_paused'
                      ? '⏳ Tiempo máximo total para usar la ficha (ej: 1h de uso en máximo 2h)'
                      : '⏳ Tiempo máximo para consumir los datos (ej: 100MB en máximo 2h)'}
                  </p>
                </div>
              )}

              {/* Registrar MAC */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="registrar_mac"
                  checked={formData.registrar_mac}
                  onChange={(e) => setFormData({ ...formData, registrar_mac: e.target.checked })}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="registrar_mac" className="text-sm font-medium text-gray-700">
                  Registrar Dirección MAC del Dispositivo (aparecerá en comentarios)
                </label>
              </div>

              {/* Velocidad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Velocidad *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Subida</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        required
                        value={formData.velocidad_subida}
                        onChange={(e) => setFormData({ ...formData, velocidad_subida: e.target.value })}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="512"
                      />
                      <select
                        value={formData.velocidad_subida_unit}
                        onChange={(e) => setFormData({ ...formData, velocidad_subida_unit: e.target.value })}
                        className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="k">k</option>
                        <option value="M">M</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Descarga</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        required
                        value={formData.velocidad_descarga}
                        onChange={(e) => setFormData({ ...formData, velocidad_descarga: e.target.value })}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="2"
                      />
                      <select
                        value={formData.velocidad_descarga_unit}
                        onChange={(e) => setFormData({ ...formData, velocidad_descarga_unit: e.target.value })}
                        className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="k">k</option>
                        <option value="M">M</option>
                      </select>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Resultado: {formData.velocidad_subida}{formData.velocidad_subida_unit}/{formData.velocidad_descarga}{formData.velocidad_descarga_unit}
                </p>
              </div>

              {/* Tipo de Usuario */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Usuario *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo_usuario: 'pin' })}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      formData.tipo_usuario === 'pin'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <p className="font-semibold">PIN</p>
                    <p className="text-xs text-gray-600 mt-1">Solo código PIN</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo_usuario: 'userpass' })}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      formData.tipo_usuario === 'userpass'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <p className="font-semibold">User & Pass</p>
                    <p className="text-xs text-gray-600 mt-1">Usuario y contraseña</p>
                  </button>
                </div>
              </div>

              {/* Tipo de Contraseña/Código */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de {formData.tipo_usuario === 'pin' ? 'Código' : 'Usuario'} *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo_codigo: 'numerico' })}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      formData.tipo_codigo === 'numerico'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <p className="font-semibold">Numérico</p>
                    <p className="text-xs text-gray-600 mt-1">Solo números (123456)</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo_codigo: 'alfanumerico' })}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      formData.tipo_codigo === 'alfanumerico'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <p className="font-semibold">Alfanumérico</p>
                    <p className="text-xs text-gray-600 mt-1">Mayúsculas + Números (AB12CD)</p>
                  </button>
                </div>
              </div>

              {/* Longitud */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Longitud *
                </label>
                <input
                  type="number"
                  min="4"
                  max="12"
                  required
                  value={formData.longitud_password}
                  onChange={(e) => setFormData({ ...formData, longitud_password: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="5"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Longitud del {formData.tipo_usuario === 'pin' ? 'código' : 'password'} (entre 4 y 12 caracteres)
                </p>
              </div>

              {/* Usar Prefijo (solo para PIN) */}
              {formData.tipo_usuario === 'pin' && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.usa_prefijo}
                      onChange={(e) => setFormData({ ...formData, usa_prefijo: e.target.checked })}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Usar prefijo en PIN</p>
                      <p className="text-xs text-gray-600">El PIN incluirá el prefijo (ej: NET123456)</p>
                    </div>
                  </label>
                </div>
              )}

              {/* Modo Consecutivo (solo para USER&PASS) */}
              {formData.tipo_usuario === 'userpass' && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.es_consecutivo}
                      onChange={(e) => setFormData({ ...formData, es_consecutivo: e.target.checked })}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">Usuarios consecutivos</p>
                      <p className="text-xs text-gray-600">Genera usuarios como {formData.prefix || 'USER'}001, {formData.prefix || 'USER'}002, etc.</p>
                    </div>
                  </label>
                </div>
              )}

              {/* Botones de Acción */}
              <div className="flex gap-4 pt-6">
                {formData.id ? (
                  // Modo Edición: Mostrar Copiar y Actualizar
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setCopyPlanName('');
                        setShowCopyModal(true);
                      }}
                      disabled={submitting}
                      className="flex-1 px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 font-semibold"
                    >
                      📋 Copiar Plan
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        setSubmitting(true);
                        setError('');
                        try {
                          const rateLimit = `${formData.velocidad_subida}${formData.velocidad_subida_unit}/${formData.velocidad_descarga}${formData.velocidad_descarga_unit}`;

                          const planPayload = {
                            router_id: formData.router_id,
                            name: formData.name,
                            plan_type: formData.plan_type,
                            price: 0,
                            rate_limit: rateLimit,
                            authentication_type: formData.tipo_usuario === 'pin' ? 'pin' : 'userpass',
                            prefix: formData.prefix || null,
                            usa_prefijo: formData.usa_prefijo,
                            tipo_codigo: formData.tipo_codigo,
                            longitud_password: parseInt(formData.longitud_password, 10),
                            es_consecutivo: formData.es_consecutivo
                          };

                          // Agregar campos según tipo de plan
                          if (formData.plan_type === 'data_limit') {
                            planPayload.data_limit = `${formData.data_limit_value}${formData.data_limit_unit}`;
                            const caducidadDays = calculateCaducidadDays();
                            planPayload.validity_days = caducidadDays;
                          } else if (formData.plan_type === 'time_running') {
                            const durationMinutes = calculateDurationMinutes();
                            planPayload.duration_minutes = durationMinutes;
                          } else if (formData.plan_type === 'time_paused') {
                            const durationMinutes = calculateDurationMinutes();
                            planPayload.duration_minutes = durationMinutes;
                            const caducidadDays = calculateCaducidadDays();
                            planPayload.validity_days = caducidadDays;
                          }

                          // Usar full-update para actualizar en Mikrotik también
                          const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
                          const token = localStorage.getItem('token');
                          await axios.put(`${API_URL}/api/plans/${formData.id}/full-update`, planPayload, {
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json'
                            }
                          });

                          setAlert({
                            type: 'success',
                            title: 'Plan Actualizado',
                            message: 'El plan ha sido actualizado exitosamente en la base de datos y en Mikrotik.',
                          });
                          
                          // Resetear formulario y volver a lista
                          setFormData({
                            id: null,
                            router_id: routers[0]?.id || '',
                            name: '',
                            plan_type: 'time_running',
                            prefix: '',
                            vigencia_dias: '',
                            vigencia_horas: '',
                            vigencia_minutos: '',
                            caducidad_dias: '',
                            caducidad_horas: '',
                            caducidad_minutos: '',
                            data_limit_value: '',
                            data_limit_unit: 'MB',
                            registrar_mac: false,
                            velocidad_subida: '1',
                            velocidad_subida_unit: 'M',
                            velocidad_descarga: '1',
                            velocidad_descarga_unit: 'M',
                            tipo_usuario: 'pin',
                            tipo_codigo: 'alfanumerico',
                            longitud_password: '6',
                            usa_prefijo: false,
                            es_consecutivo: false
                          });
                          
                          setActiveTab('listar');
                          await loadData();
                        } catch (err) {
                          console.error('Error updating plan:', err);
                          const errorMessage = err.response?.data?.detail || err.message || 'Error al actualizar plan';
                          setError(errorMessage);
                        } finally {
                          setSubmitting(false);
                        }
                      }}
                      disabled={submitting}
                      className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 font-semibold"
                    >
                      {submitting ? 'Actualizando...' : '✓ Actualizar Plan'}
                    </button>
                  </>
                ) : (
                  // Modo Creación: Mostrar Limpiar y Crear
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({
                          id: null,
                          router_id: routers[0]?.id || '',
                          name: '',
                          plan_type: 'time_running',
                          prefix: '',
                          vigencia_dias: '',
                          vigencia_horas: '',
                          vigencia_minutos: '',
                          caducidad_dias: '',
                          caducidad_horas: '',
                          caducidad_minutos: '',
                          data_limit_value: '',
                          data_limit_unit: 'MB',
                          registrar_mac: false,
                          velocidad_subida: '1',
                          velocidad_subida_unit: 'M',
                          velocidad_descarga: '1',
                          velocidad_descarga_unit: 'M',
                          tipo_usuario: 'pin',
                          tipo_codigo: 'alfanumerico',
                          longitud_password: '6',
                          usa_prefijo: false,
                          es_consecutivo: false
                        });
                        setError('');
                      }}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                    >
                      Limpiar Formulario
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-semibold"
                    >
                      {submitting ? 'Creando Plan...' : 'Crear Plan'}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        )}

        {/* Listar Planes Tab */}
        {activeTab === 'listar' && (
          <>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : plans.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay planes</h3>
                <p className="text-gray-600 mb-6">Crea tu primer plan de internet</p>
                <button
                  onClick={() => setShowWizard(true)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Crear Plan
                </button>
              </div>
            ) : (
              <>
                {/* Filtros por Tipo de Plan */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedTypeFilter('all')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedTypeFilter === 'all'
                          ? 'bg-blue-600 text-white'
                          : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Todos ({plans.filter(p => p.is_active !== false).length})
                    </button>
                    <button
                      onClick={() => setSelectedTypeFilter('time_running')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                        selectedTypeFilter === 'time_running'
                          ? 'bg-blue-600 text-white'
                          : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>⏱️</span>
                      Tiempo Corrido ({plans.filter(p => p.plan_type === 'time_running' && p.is_active !== false).length})
                    </button>
                    <button
                      onClick={() => setSelectedTypeFilter('time_paused')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                        selectedTypeFilter === 'time_paused'
                          ? 'bg-orange-600 text-white'
                          : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>⏸️</span>
                      Tiempo Pausado ({plans.filter(p => p.plan_type === 'time_paused' && p.is_active !== false).length})
                    </button>
                    <button
                      onClick={() => setSelectedTypeFilter('data_limit')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                        selectedTypeFilter === 'data_limit'
                          ? 'bg-green-600 text-white'
                          : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>📊</span>
                      Por Consumo ({plans.filter(p => p.plan_type === 'data_limit' && p.is_active !== false).length})
                    </button>
                    <button
                      onClick={() => setSelectedTypeFilter('suspended')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                        selectedTypeFilter === 'suspended'
                          ? 'bg-orange-600 text-white'
                          : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>⏸️</span>
                      Suspendidos ({plans.filter(p => p.is_active === false).length})
                    </button>
                  </div>
                </div>

                {/* Barra de Búsqueda */}
                <div className="mb-6">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="🔍 Buscar plan por nombre..."
                      className={`w-full px-4 py-3 pl-10 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>

                {/* Tabs de filtrado por router */}
                {routers.length > 1 && (
                  <div className="mb-6 flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedRouterFilter('all')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedRouterFilter === 'all'
                          ? 'bg-blue-600 text-white'
                          : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Todos Routers ({plans.length})
                    </button>
                    {routers.map((router) => {
                      const routerPlans = plans.filter(p => p.router_id === router.id);
                      const colors = getRouterColor(router.id, routers);
                      return (
                        <button
                          key={router.id}
                          onClick={() => setSelectedRouterFilter(router.id)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            selectedRouterFilter === router.id
                              ? `${colors.badge} text-white`
                              : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {router.name} ({routerPlans.length})
                        </button>
                      );
                    })}
                  </div>
                )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {plans
                  .filter(plan => {
                    // Filtro por router
                    const matchesRouter = selectedRouterFilter === 'all' || plan.router_id === selectedRouterFilter;
                    // Filtro por tipo Y estado
                    let matchesType;
                    if (selectedTypeFilter === 'suspended') {
                      // Tab "Suspendidos" - mostrar SOLO planes inactivos
                      matchesType = plan.is_active === false;
                    } else if (selectedTypeFilter === 'all') {
                      // Tab "Todos" - mostrar SOLO planes activos (no suspendidos)
                      matchesType = plan.is_active !== false;
                    } else {
                      // Tabs específicos (Tiempo Corrido, etc.) - mostrar solo activos de ese tipo
                      matchesType = plan.plan_type === selectedTypeFilter && plan.is_active !== false;
                    }
                    // Filtro por búsqueda
                    const matchesSearch = searchQuery === '' || plan.name.toLowerCase().includes(searchQuery.toLowerCase());
                    
                    return matchesRouter && matchesType && matchesSearch;
                  })
                  .map((plan) => {
                  const router = routers.find(r => r.id === plan.router_id);
                  const colors = getRouterColor(plan.router_id, routers);
                  const routerName = getRouterName(plan.router_id, routers);
                  const badge = getPlanTypeBadge(plan.plan_type);
                  
                  return (
                    <div
                      key={plan.id}
                      className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden border-2 ${colors.border} cursor-pointer relative ${!plan.is_active ? 'opacity-50' : ''}`}
                      style={!plan.is_active ? {filter: 'grayscale(50%)'} : {}}
                    >
                      {/* Badge de estado suspendido */}
                      {!plan.is_active && (
                        <div className="absolute top-2 right-2 z-10 bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                          SUSPENDIDO
                        </div>
                      )}
                      {/* Botón suspender/reactivar (esquina superior derecha) */}
                      <button
                        onClick={(e) => handleDeleteClick(plan, e)}
                        className={`absolute ${!plan.is_active ? 'top-12' : 'top-2'} right-2 z-10 p-2 ${
                          !plan.is_active 
                            ? 'bg-green-500 hover:bg-green-600' 
                            : 'bg-orange-500 hover:bg-orange-600'
                        } text-white rounded-lg transition-colors shadow-md`}
                        title={!plan.is_active ? "Reactivar plan" : "Suspender plan"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      
                      <div onClick={() => {
                        const planeType = plan.plan_type || 'time_running';
                        const authType = plan.authentication_type || 'pin';

                        const totalMinutes = plan.duration_minutes || 0;
                        const dias = Math.floor(totalMinutes / (24 * 60));
                        const horas = Math.floor((totalMinutes % (24 * 60)) / 60);
                        const minutos = totalMinutes % 60;

                        const defaultRate = '512k/2M';
                        const rate = plan.rate_limit || defaultRate;
                        const parts = rate.split('/');
                        const upRaw = parts[0] || '512k';
                        const downRaw = parts[1] || '2M';
                        const upNum = upRaw.replace(/[^0-9]/g, '') || '512';
                        const upUnit = upRaw.replace(/[0-9]/g, '') || 'k';
                        const downNum = downRaw.replace(/[^0-9]/g, '') || '2';
                        const downUnit = downRaw.replace(/[0-9]/g, '') || 'M';

                        setFormData({
                          id: plan.id,
                          router_id: plan.router_id,
                          name: plan.name,
                          plan_type: planeType,
                          prefix: plan.prefix || '',
                          vigencia_dias: dias ? String(dias) : '',
                          vigencia_horas: horas ? String(horas) : '',
                          vigencia_minutos: minutos ? String(minutos) : '',
                          registrar_mac: false,
                          velocidad_subida: upNum,
                          velocidad_subida_unit: upUnit,
                          velocidad_descarga: downNum,
                          velocidad_descarga_unit: downUnit,
                          tipo_usuario: authType === 'pin' ? 'pin' : 'userpass',
                          tipo_codigo: plan.tipo_codigo || 'alfanumerico',
                          longitud_password: String(plan.longitud_password || 6),
                          usa_prefijo: !!plan.usa_prefijo,
                          es_consecutivo: !!plan.es_consecutivo,
                        });

                        setActiveTab('crear');
                      }}
                    >
                      {/* Badge de router */}
                      <div className={`${colors.badge} text-white text-xs font-semibold px-3 py-1`}>
                        {routerName}
                      </div>
                      
                      <div className="p-6">
                        {/* Badge de Tipo de Plan */}
                        <div className="mb-3">
                          <span className={`inline-flex items-center gap-2 px-3 py-1 text-sm font-semibold rounded-full border ${badge.bgColor} ${badge.textColor} ${badge.borderColor}`}>
                            <span>{badge.icon}</span>
                            {badge.label}
                          </span>
                        </div>

                        <div className="mb-4">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                          <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                            {plan.created_with_version?.toUpperCase() || 'v6'}
                          </span>
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">⏱️</span>
                            <span className="font-semibold">{plan.duration_minutes || 0} minutos</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">🚀</span>
                            <span className="font-semibold font-mono text-xs">{plan.rate_limit}</span>
                          </div>
                        </div>

                        {plan.needs_migration && (
                          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-xs text-amber-800 font-semibold">
                              ⚠️ Requiere migración
                            </p>
                          </div>
                        )}
                      </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            )}
          </>
        )}
      </div>



      {/* Modal de Eliminación de Plan */}
      {/* Modal: Plan Duplicado */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            {/* Header */}
            <div className="bg-yellow-500 text-white p-6">
              <div className="flex items-center gap-3">
                <Info className="w-8 h-8" />
                <div>
                  <h2 className="text-2xl font-bold">Plan Duplicado</h2>
                  <p className="text-yellow-100 text-sm">Ya existe un plan con este nombre</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <div className={`p-4 rounded-lg ${duplicatePlanIsActive ? 'bg-gray-50' : 'bg-red-50 border border-red-200'}`}>
                <p className="text-gray-700 text-sm">
                  {duplicatePlanIsActive ? (
                    <>Ya existe un plan <span className="font-bold text-green-600">activo</span> con el nombre <span className="font-bold">"{formData.name}"</span> en este router.</>
                  ) : (
                    <>Existe un plan <span className="font-bold text-red-600">eliminado (inactivo)</span> con el nombre <span className="font-bold">"{formData.name}"</span> en este router.</>
                  )}
                </p>
              </div>

              <p className="text-gray-700 font-medium">¿Qué deseas hacer?</p>

              {/* Opción 1: Actualizar/Reactivar existente */}
              <button
                onClick={handleUpdateExistingPlan}
                disabled={submitting}
                className="w-full p-4 bg-blue-50 border-2 border-blue-300 rounded-lg hover:bg-blue-100 transition-colors text-left disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                    1
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-blue-900">
                      {duplicatePlanIsActive ? 'Actualizar Plan Existente' : 'Reactivar y Actualizar Plan'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {duplicatePlanIsActive 
                        ? 'Sobrescribir el plan actual con los nuevos valores del formulario'
                        : 'Reactivar el plan eliminado y actualizarlo con los nuevos valores (se recreará en Mikrotik)'}
                    </p>
                  </div>
                </div>
              </button>

              {/* Opción 2: Crear con nuevo nombre */}
              <div className="p-4 bg-green-50 border-2 border-green-300 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-green-900">Crear Nuevo Plan</p>
                    <p className="text-sm text-gray-600">Mantener el existente y crear uno nuevo con otro nombre</p>
                  </div>
                </div>
                <input
                  type="text"
                  value={newPlanName}
                  onChange={(e) => setNewPlanName(e.target.value.toUpperCase())}
                  placeholder="Ej: 1 HORA V2, 1 HORA NUEVO"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <button
                  onClick={handleCreateWithNewName}
                  disabled={submitting || !newPlanName}
                  className="w-full mt-3 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Creando...' : 'Crear con este nombre'}
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-4 flex justify-end">
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicatePlanId(null);
                  setDuplicatePlanIsActive(true);
                  setNewPlanName('');
                }}
                disabled={submitting}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && deleteInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className={`${!planToDelete.is_active ? 'bg-green-500' : 'bg-orange-500'} text-white p-6`}>
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8" />
                <div>
                  <h2 className="text-2xl font-bold">{!planToDelete.is_active ? 'Reactivar' : 'Suspender'} Plan</h2>
                  <p className={`${!planToDelete.is_active ? 'text-green-100' : 'text-orange-100'} text-sm`}>
                    {!planToDelete.is_active ? 'El plan volverá a estar disponible' : 'El plan dejará de estar disponible'}
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              {/* Info del plan */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg text-gray-900 mb-2">Plan: {planToDelete.name}</h3>
                <p className="text-sm text-gray-600">Router: {routers.find(r => r.id === planToDelete.router_id)?.name || 'N/A'}</p>
              </div>

              {/* Estadísticas */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-600">Fichas Generadas</p>
                  <p className="text-2xl font-bold text-blue-600">{deleteInfo.vouchers_total}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {deleteInfo.vouchers_unused} sin usar · {deleteInfo.vouchers_used} usadas
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-sm text-gray-600">Ventas Registradas</p>
                  <p className="text-2xl font-bold text-purple-600">{deleteInfo.sales_count}</p>
                  <p className="text-xs text-gray-500 mt-1">Se conservan como respaldo</p>
                </div>
                {deleteInfo.active_users_count > 0 && (
                  <div className="col-span-2 bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <p className="text-sm text-gray-600">Usuarios Activos en Mikrotik</p>
                    <p className="text-2xl font-bold text-orange-600">{deleteInfo.active_users_count}</p>
                    <p className="text-xs text-gray-500 mt-1">Serán desconectados</p>
                  </div>
                )}
              </div>

              {/* Advertencias */}
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  ⚠️ Se realizarán las siguientes acciones:
                </h4>
                <ul className="space-y-2">
                  {deleteInfo.warnings.map((warning, idx) => (
                    <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-yellow-600 mt-0.5">•</span>
                      <span>{warning}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Usuarios activos detalle */}
              {deleteInfo.active_users && deleteInfo.active_users.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    🔴 Usuarios que serán desconectados:
                  </h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {deleteInfo.active_users.map((user, idx) => (
                      <div key={idx} className="text-sm text-gray-700 flex justify-between">
                        <span className="font-mono">{user.username}</span>
                        <span className="text-gray-500">{user.ip} · {user.uptime}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setPlanToDelete(null);
                  setDeleteInfo(null);
                }}
                disabled={deleting}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeletePlan}
                disabled={deleting}
                className={`px-6 py-3 ${!planToDelete.is_active ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'} text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2`}
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {!planToDelete.is_active ? 'Reactivando...' : 'Suspendiendo...'}
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    {!planToDelete.is_active ? 'Sí, Reactivar Plan' : 'Sí, Suspender Plan'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Copiar Plan */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="bg-blue-500 text-white p-6">
              <h2 className="text-2xl font-bold">📋 Copiar Plan</h2>
              <p className="text-blue-100 text-sm">Crear una copia con las mismas características</p>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Plan original:</strong> {formData.name}
                </p>
                <p className="text-xs text-gray-600">
                  Se creará un nuevo plan con las mismas características. Solo necesitas especificar un nombre diferente.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Nuevo Plan *
                </label>
                <input
                  type="text"
                  value={copyPlanName}
                  onChange={(e) => setCopyPlanName(e.target.value.toUpperCase())}
                  placeholder="Ej: PLAN COPIA, PLAN V2"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
                {error && copyPlanName && (
                  <p className="text-red-500 text-sm mt-1">{error}</p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowCopyModal(false);
                  setCopyPlanName('');
                  setError('');
                }}
                disabled={submitting}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCopyPlan}
                disabled={submitting || !copyPlanName}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Copiando...' : 'Crear Copia'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Wizard Modal */}
      <PlanWizardModal
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        routers={routers}
        onSubmit={handleWizardSubmit}
        existingPlans={plans}
      />

      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default PlansPage;
